<script lang="ts" setup>
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import type { IButtonShape, IButtonSize, IButtonVariant } from "@/types"
import { Icon } from "@/components/ui"

interface IButtonProps {
  variant?: IButtonVariant
  size?: IButtonSize
  shape?: IButtonShape
  href?: string
  disabled?: boolean
  prefixIcon?: IconKeys
  suffixIcon?: IconKeys
}

withDefaults(defineProps<IButtonProps>(), {
  variant: "primary",
  size: "default",
  shape: "default",
  disabled: false,
})
</script>

<template>
  <component
    :is="href ? 'a' : 'button'" :href="href" v-bind="$attrs" class="gpx-button"
    :class="[
      `gpx-button_${variant}`,
      `gpx-button-${size}`,
      shape !== 'default' ? `gpx-button_shape_${shape}` : null,
    ]" :disabled="disabled"
  >
    <slot name="prefixIcon">
      <Icon v-if="prefixIcon" :icon="prefixIcon" :size="size" />
    </slot>
    <slot />
    <slot name="suffixIcon">
      <Icon v-if="suffixIcon" :icon="suffixIcon" :size="size" />
    </slot>
  </component>
</template>

<style lang="postcss">
@reference "../../../assets/styles.css";

.gpx-button {
  transition:
    color 0.15s ease-in-out,
    background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;

  @apply cursor-pointer rounded-lg font-comfortaa font-bold text-base py-2 px-4 border border-transparent bg-transparent gap-2 text-center whitespace-nowrap no-underline flex items-center justify-center;
}

a.gpx-button {
  text-decoration: none;
}

.gpx-button-xs {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  gap: 0.25rem;
}

.gpx-button-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  gap: 0.375rem;
}

.gpx-button-lg {
  padding: 0.5rem 1.5rem;
  font-size: 1.25rem;
  gap: 1rem;
}

.gpx-button_flat {
  @apply text-gray-700;
}

.gpx-button_flat:hover {
  @apply text-gray-400;
}

.gpx-button_danger {
  @apply bg-theme-danger text-white;
}

.gpx-button_danger:hover {
  @apply text-theme-danger bg-white;
}

.gpx-button_primary {
  @apply bg-primary text-primary-btn-text border-primary;
}

.gpx-button_primary:hover {
  @apply bg-white text-primary border-primary;
}

.gpx-button_success {
  @apply bg-theme-success text-white border-theme-success;
}

.gpx-button_success:hover {
  @apply bg-white text-theme-success border-theme-success;
}

.gpx-button_accent {
  @apply bg-nav-active text-white border-nav-active;
}

.gpx-button_accent:hover {
  @apply bg-white text-nav-active border-nav-active;
}

.gpx-button_icon {
  padding: 0.5rem 0.675rem;
}

.gpx-button_tertiary,
.gpx-button_icon {
  @apply bg-white;

  color: var(--template-primary-colour);
  border-color: var(--template-primary-colour);
}

.gpx-button_tertiary:hover,
.gpx-button_icon:hover {
  @apply text-white;

  background-color: var(--template-primary-colour);
  border-color: var(--template-primary-colour);
}

.gpx-button_secondary {
  background: var(--template-secondary-colour);
  color: var(--template-secondary-button-text);
  border-color: var(--template-secondary-colour);
}

.gpx-button_secondary:hover {
  @apply bg-white;

  color: var(--template-primary-colour);
  border-color: var(--template-secondary-colour);
}

.gpx-button:disabled,
.gpx-button:disabled:hover {
  @apply bg-stone-400 border-stone-400 text-gray-100 cursor-not-allowed;
}

/* Shape modifiers (opt-in; base .gpx-button is unchanged when shape is default) */

.gpx-button.gpx-button_shape_circle {
  @apply min-h-0 min-w-0 shrink-0 gap-0 rounded-full p-0 aspect-square;
}

.gpx-button.gpx-button_shape_circle.gpx-button-xs {
  @apply size-8;
}

.gpx-button.gpx-button_shape_circle.gpx-button-sm {
  @apply size-10;
}

.gpx-button.gpx-button_shape_circle.gpx-button-default {
  @apply size-12;
}

.gpx-button.gpx-button_shape_circle.gpx-button-lg {
  @apply size-14;
}

.gpx-button.gpx-button_shape_pill {
  @apply rounded-full;
}

.gpx-button.gpx-button_shape_pillLg {
  @apply rounded-full py-3 px-6 text-lg;
}
</style>
